import { MobileList } from '@/components/app/list-page';
import type { ListPageConfig } from '@/components/app/list-page';
import { useSidePanel } from '@/components/app/side-panel';
import { StateError } from '@/components/app/state-error';
import { StateLoading } from '@/components/app/state-loading';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Ad } from '@/features/meta-ads/api/types';
import { Megaphone, Plus, Search, XIcon } from 'lucide-react';
import { CampaignDestinations } from '../campaign-list/campaign-destinations';

/** Compact rendering of the same ad rows and actions inside Campaigns. */
export function AdsPanelList({
  config,
  destinations,
}: {
  config: ListPageConfig<Ad>;
  destinations: string[];
}) {
  const { close } = useSidePanel();
  return (
    <section
      className="flex h-full min-h-0 flex-col bg-muted/15"
      aria-label="Campaign details"
    >
      <div className="border-b bg-background p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
            <Megaphone aria-hidden className="size-5" />
          </div>
          <div className="min-w-0 flex-1 py-0.5">
            <h2 className="break-words text-lg font-semibold leading-snug tracking-tight">
              {config.title}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="-mr-2 -mt-1 size-8 shrink-0 text-muted-foreground"
            onClick={close}
            aria-label="Close campaign details"
          >
            <XIcon className="size-4" />
          </Button>
        </div>
        <div className="mt-4">
          <CampaignDestinations labels={destinations} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {config.primaryAction && (
            <Button size="sm" onClick={config.primaryAction.onClick}>
              <Plus aria-hidden className="size-4" />
              {config.primaryAction.label}
            </Button>
          )}
          {config.toolbar}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Ads</h3>
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
            {config.rows.length}
          </span>
        </div>
        <div className="relative">
          <Search
            aria-hidden
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            className="h-9 bg-background pl-9"
            aria-label="Search ads"
            placeholder="Search ads"
            value={config.search}
            onChange={(event) => config.onSearchChange?.(event.target.value)}
          />
        </div>
        {config.isLoading ? (
          <StateLoading />
        ) : config.isError ? (
          <StateError message={config.errorMessage} onRetry={config.onRetry} />
        ) : config.rows.length > 0 ? (
          <MobileList
            columns={config.columns.filter(
              (column) => column.mobile !== 'trailing'
            )}
            rows={config.rows}
            rowKey={config.rowKey}
            onRowClick={config.onRowClick}
            rowActions={config.rowActions}
          />
        ) : (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="font-medium">{config.empty?.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {config.empty?.description}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
